import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { ArrowLeft, MapIcon } from "lucide-react";
import { listingService } from "@/lib/services";
import { PageShell } from "@/components/nav";
import { ListingDetailContent } from "@/components/listing-detail-content";
import { ListingImageCarousel } from "@/components/listing-image-carousel";
import { ListingBookingPanel } from "@/components/listing-booking-panel";
import { ExplorerListingRail } from "@/components/explorer-listing-rail";
import { ExplorerMap, type MapBounds } from "@/components/explorer-map";
import { MobileExplorerSheet } from "@/components/mobile-explorer-sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import { fromISODate, toISODate } from "@/lib/date";
import type { StayRange } from "@/components/stay-calendar";
import type { Listing } from "@/types/api";

const searchSchema = z.object({
  checkIn: z.string().optional().catch(undefined),
  checkOut: z.string().optional().catch(undefined),
  guests: z.coerce.number().int().min(1).max(30).optional().catch(undefined),
});

export const Route = createFileRoute("/listings/$listingId")({
  validateSearch: searchSchema,
  loader: ({ context: { queryClient }, params }) =>
    queryClient.ensureQueryData({
      queryKey: ["listing", params.listingId],
      queryFn: () => listingService.get(params.listingId),
    }),
  component: ListingExplorerPage,
});

/** How wide an area to load around the opened stay, in kilometres. */
const AREA_RADIUS_KM = 25;

/** Pins to hold at once. Enough to browse, few enough to stay smooth. */
const AREA_PAGE_SIZE = 40;

function ListingExplorerPage() {
  const { listingId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  /*
    The map viewport, once the guest has asked for results in an area they
    moved to. Until then the area is a radius around the stay they opened.
  */
  const [bounds, setBounds] = useState<MapBounds | null>(null);

  // ── The opened stay ─────────────────────────────────────────────────
  const listingQuery = useQuery({
    queryKey: ["listing", listingId],
    queryFn: ({ signal }) => listingService.get(listingId, signal),
    staleTime: 60_000,
  });

  const listing = listingQuery.data?.data ?? null;

  const anchor = useMemo(() => {
    const lat = Number.parseFloat(listing?.property?.lat ?? "");
    const lng = Number.parseFloat(listing?.property?.lng ?? "");
    return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
  }, [listing]);

  // ── Everything else in the area ─────────────────────────────────────
  /*
    One request replaces what used to be a nine-request waterfall: a nearby
    properties call, then an individual fetch for each property whose listings
    the list endpoint had not included. Those follow-up requests were capped at
    eight, so the map silently hid whatever came after them.

    The catalogue already returns a complete listing, so a pin tapped here has
    everything the detail panel needs — selecting one costs no request at all.
  */
  const areaQuery = useQuery({
    queryKey: ["explorer-area", bounds, anchor, search.checkIn, search.checkOut, search.guests],
    enabled: Boolean(anchor) || Boolean(bounds),
    queryFn: ({ signal }) =>
      listingService.list(
        {
          ...(bounds ?? { lat: anchor!.lat, lng: anchor!.lng, radius: AREA_RADIUS_KM }),
          check_in: search.checkIn && search.checkOut ? search.checkIn : undefined,
          check_out: search.checkIn && search.checkOut ? search.checkOut : undefined,
          guests: search.guests,
          per_page: AREA_PAGE_SIZE,
        },
        signal,
      ),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });

  const areaData = areaQuery.data;

  /*
    The opened stay is always pinned, even when it falls outside the area the
    guest has panned to — losing the thing you are looking at is disorienting.
  */
  const pinnedListings = useMemo<Listing[]>(() => {
    const area = areaData?.data ?? [];
    if (!listing) return area;
    return area.some((item) => item.id === listing.id) ? area : [listing, ...area];
  }, [listing, areaData]);

  // ── Selection ───────────────────────────────────────────────────────
  const selectListing = useCallback(
    (next: Listing) => {
      if (next.id === listingId) return;
      /*
        Routed, not pushed through history.replaceState. The old code wrote the
        URL directly, which left the router's own state pointing at the previous
        listing — so the back button and any later navigation disagreed with the
        address bar.
      */
      navigate({
        to: "/listings/$listingId",
        params: { listingId: next.id },
        search: (prev) => prev,
        replace: true,
        resetScroll: false,
      });
    },
    [navigate, listingId],
  );

  const handleSearchArea = useCallback((next: MapBounds) => setBounds(next), []);

  // ── Stay details in the URL ─────────────────────────────────────────
  const range: StayRange = useMemo(
    () => ({ from: fromISODate(search.checkIn), to: fromISODate(search.checkOut) }),
    [search.checkIn, search.checkOut],
  );

  const setRange = useCallback(
    (next: StayRange) => {
      navigate({
        to: ".",
        search: (prev) => ({
          ...prev,
          checkIn: next.from ? toISODate(next.from) : undefined,
          checkOut: next.to ? toISODate(next.to) : undefined,
        }),
        replace: true,
        resetScroll: false,
      });
    },
    [navigate],
  );

  const guests = search.guests ?? 1;

  const setGuests = useCallback(
    (next: number) => {
      navigate({
        to: ".",
        search: (prev) => ({ ...prev, guests: next > 1 ? next : undefined }),
        replace: true,
        resetScroll: false,
      });
    },
    [navigate],
  );

  const focus = anchor ? { ...anchor, key: listingId } : null;

  if (listingQuery.isPending || !listing) {
    return <ExplorerSkeleton />;
  }

  /*
    A circle instead of a labelled link. The map is the point of this page, and
    on desktop the control sits in the map's overlay row next to the place
    search — a text button there costs width that the search field can use, and
    a back arrow needs no label to be understood.
  */
  const backButton = (
    <Link
      to="/"
      aria-label="Back to discover"
      title="Back to discover"
      className="grid h-11 w-11 place-items-center rounded-full border border-border/60 bg-card/80 text-ink shadow-[var(--shadow-modal)] backdrop-blur transition hover:bg-card active:scale-95"
    >
      <ArrowLeft className="h-4 w-4" strokeWidth={2.2} />
    </Link>
  );

  const map = (
    <ExplorerMap
      listings={pinnedListings}
      activeListingId={listing.id}
      onSelect={selectListing}
      focus={focus}
      onSearchArea={handleSearchArea}
      searching={areaQuery.isFetching}
      leading={backButton}
    />
  );

  const rail = (
    <ExplorerListingRail
      listings={pinnedListings}
      activeId={listing.id}
      onSelect={selectListing}
      loading={areaQuery.isPending}
    />
  );

  if (isMobile) {
    return (
      <PageShell chrome="focused">
        <div className="relative h-[calc(100dvh-72px)] w-full overflow-hidden">
          <div className="absolute inset-0 z-0">{map}</div>

          <MobileExplorerSheet
            initial="collapsed"
            /*
              The rail and the booking bar stay visible at every height, so a
              guest can compare nearby stays and reserve one without ever
              opening the sheet — the map stays on screen throughout.
            */
            header={
              <>
                {rail}
                <ListingBookingPanel
                  listing={listing}
                  range={range}
                  onRangeChange={setRange}
                  guests={guests}
                  onGuestsChange={setGuests}
                  compact
                />
              </>
            }
          >
            <ListingImageCarousel
              images={listing.images ?? []}
              alt={listing.title}
              resetKey={listing.id}
            />
            <ListingDetailContent listing={listing} />
          </MobileExplorerSheet>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell chrome="focused">
      <div className="flex h-[calc(100vh-72px)] w-full">
        <aside className="flex h-full w-[440px] shrink-0 flex-col border-r border-border bg-background">
          <div className="min-h-0 flex-1 overflow-y-auto">
            <ListingImageCarousel
              images={listing.images ?? []}
              alt={listing.title}
              resetKey={listing.id}
            />
            <div className="px-4 pt-4 md:px-6">
              <ListingBookingPanel
                listing={listing}
                range={range}
                onRangeChange={setRange}
                guests={guests}
                onGuestsChange={setGuests}
              />
            </div>
            <ListingDetailContent listing={listing} />
          </div>

          <div className="shrink-0 border-t border-border bg-background/95 backdrop-blur">
            <p className="flex items-center gap-1.5 px-4 pt-2 text-[10px] font-bold uppercase tracking-[0.16em] text-ink-subtle">
              <MapIcon className="h-3 w-3" />
              {areaQuery.isPending
                ? "Finding nearby stays…"
                : `${pinnedListings.length} stays in this area`}
            </p>
            {rail}
          </div>
        </aside>

        <div className="relative h-full flex-1">{map}</div>
      </div>
    </PageShell>
  );
}

function ExplorerSkeleton() {
  return (
    <PageShell chrome="focused">
      <div className="flex h-[calc(100vh-72px)] w-full">
        <div className="w-full space-y-4 p-4 lg:w-[440px] lg:shrink-0 lg:border-r lg:border-border">
          <div className="h-64 animate-pulse rounded-2xl brand-skeleton" />
          <div className="h-8 w-2/3 animate-pulse rounded-full brand-skeleton" />
          <div className="h-4 w-1/3 animate-pulse rounded-full brand-skeleton" />
          <div className="h-32 animate-pulse rounded-2xl brand-skeleton" />
        </div>
        <div className="hidden flex-1 animate-pulse bg-muted lg:block" />
      </div>
    </PageShell>
  );
}
